/**
 * Meta Verified Business Tech Provider badge.
 * Displayed across signup, OAuth-connect, and landing surfaces as a trust signal.
 *
 * Variants:
 *   - "compact"  : tiny inline badge for tight spaces (auth pages, sidebars)
 *   - "default"  : medium horizontal pill (dashboard, footer)
 *   - "showcase" : large square tile with caption (landing hero)
 */
export function MetaVerifiedBadge({ variant = "default", className = "" }) {
  const src = "/badges/meta-verified-tech-provider.png";
  const alt = "Meta Verified Business Tech Provider";

  if (variant === "compact") {
    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0F1A33] border border-[#1f3a6b] ${className}`}
        data-testid="meta-verified-badge-compact"
        title={alt}
      >
        <img src={src} alt={alt} className="w-5 h-5 object-contain" loading="lazy" />
        <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-white/85 leading-none whitespace-nowrap">
          Meta Verified
          <span className="hidden sm:inline text-white/55 font-medium normal-case tracking-normal"> · Business Tech Provider</span>
        </span>
      </div>
    );
  }

  if (variant === "showcase") {
    return (
      <a
        href="https://developers.facebook.com/products/business/"
        target="_blank"
        rel="noopener noreferrer"
        className={`group inline-flex flex-col items-center gap-3 ${className}`}
        data-testid="meta-verified-badge-showcase"
      >
        <div className="w-32 h-32 rounded-sm overflow-hidden bg-[#0B1A33] border border-[#1f3a6b] shadow-[0_0_30px_rgba(56,124,255,0.25)] group-hover:shadow-[0_0_40px_rgba(56,124,255,0.45)] transition-shadow">
          <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-white">Meta Verified</div>
          <div className="text-[11px] text-white/60 mt-1">Business Tech Provider</div>
        </div>
      </a>
    );
  }

  // default
  return (
    <div
      className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-sm bg-[#0F1A33] border border-[#1f3a6b] ${className}`}
      data-testid="meta-verified-badge"
      title={alt}
    >
      <img src={src} alt={alt} className="w-7 h-7 object-contain shrink-0" loading="lazy" />
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/95">Meta Verified</div>
        <div className="text-[10px] text-white/60 mt-0.5">Business Tech Provider</div>
      </div>
    </div>
  );
}
